// microfrontend-auth-handler.js
// Handler para recibir y manejar tokens de autenticación desde el frontend padre

class MicrofrontendAuthHandler {
    constructor() {
        this.token = null;
        this.user = null;
        this.tokenReceivedCallbacks = [];
        this.isReady = false;

        // Promesa que se resuelve cuando llega el primer token (evita race conditions con Apollo)
        this._tokenPromiseResolve = null;
        this.tokenPromise = new Promise(resolve => {
            this._tokenPromiseResolve = resolve;
        });
        
        // Configurar listener para PostMessage
        this.setupPostMessageListener();
        
        // Intentar obtener token de query parameters como fallback
        this.tryGetTokenFromUrl();
        
        // Notificar al frontend padre que estamos listos
        this.notifyParentReady();
    }

    // Configurar listener para mensajes del frontend padre
    setupPostMessageListener() {
        window.addEventListener('message', (event) => {
            // Filtrar mensajes internos de herramientas de desarrollo y mensajes vacíos/undefined
            if (!event.data || 
                event.data === undefined || 
                event.data === null ||
                typeof event.data === 'string') {
                return; // Ignorar silenciosamente mensajes vacíos y strings
            }

            if (event.data && typeof event.data === 'object') {
                // Ignorar mensajes de Apollo DevTools y otras herramientas internas
                if (event.data.__apollo || 
                    event.data.type === 'webpackHotUpdate' ||
                    event.data.type === 'vite:hmr' ||
                    event.data.source === 'react-devtools' ||
                    event.data.source === 'vue-devtools' ||
                    event.data.source === 'apollo-devtools' ||
                    event.data.__APOLLO_DEVTOOLS__ ||
                    event.data.type === 'SELENIUM_IDE_CS_MSG' ||
                    event.data.type?.startsWith?.('SELENIUM') ||
                    event.origin === window.location.origin ||
                    event.source === window) {
                    return; // Ignorar silenciosamente
                }

                // Ignorar mensajes que no tienen un tipo definido o válido
                if (!event.data.type || 
                    (event.data.type !== 'AUTH_TOKEN' && 
                     event.data.type !== 'REQUEST_AUTH_TOKEN' && 
                     event.data.type !== 'AUTH_REFRESH' &&
                     event.data.type !== 'MICROFRONTEND_READY')) {
                    return; // Solo procesar mensajes de autenticación válidos
                }
            }
            
            // Verificar orígenes permitidos según el entorno para mensajes de autenticación
            // Se pueden configurar mediante la variable Vite: VITE_ALLOWED_ORIGINS (coma-separada)
            const rawAllowed = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_ALLOWED_ORIGINS)
                ? String(import.meta.env.VITE_ALLOWED_ORIGINS)
                : '';

            let allowedOrigins = [];
            if (rawAllowed) {
                // Si se define la lista en VITE_ALLOWED_ORIGINS la usamos tal cual
                allowedOrigins = rawAllowed.split(',').map(s => s.trim()).filter(Boolean);
            } else {
                // Fallback para desarrollo local únicamente
                allowedOrigins = ['http://localhost:8080', 'http://127.0.0.1:8080'];
            }

            // Añadir explícitamente el origen actual como permitido (útil en entornos donde el padre y el microfrontend comparten dominio)
            try { if (window && window.location && window.location.origin) allowedOrigins.push(window.location.origin); } catch(e) {}

            const isAllowedOrigin = allowedOrigins.some(origin => event.origin.startsWith(origin));
            
            if (!isAllowedOrigin) {
                return;
            }
            
            // Solo procesar mensajes de autenticación
            
            if (event.data.type === 'AUTH_TOKEN') {
                // Manejar diferentes formatos de mensaje
                let token = null;
                let user = null;
                
                // Formato con payload (desde localStorage/frontend padre)
                if (event.data.payload) {
                    token = event.data.payload.token || event.data.payload.accessToken;
                    user = event.data.payload.user;
                }
                // Formato directo
                else {
                    token = event.data.token;
                    user = event.data.user;
                }
                
                this.setToken(token, user);
            }
            
            if (event.data.type === 'AUTH_REFRESH') {
                // Manejar refresh con el mismo formato
                let token = null;
                let user = null;
                
                if (event.data.payload) {
                    token = event.data.payload.token || event.data.payload.accessToken;
                    user = event.data.payload.user;
                } else {
                    token = event.data.token;
                    user = event.data.user;
                }
                
                this.setToken(token, user);
            }
        });
    }

    // Obtener token de query parameters como método alternativo
    tryGetTokenFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get('access_token');
        const userFromUrl = urlParams.get('user_data');
        
        if (tokenFromUrl) {
            const userData = userFromUrl ? JSON.parse(decodeURIComponent(userFromUrl)) : null;
            this.setToken(tokenFromUrl, userData);
        }
    }

    // Notificar al frontend padre que estamos listos para recibir el token
    notifyParentReady() {
        if (window.parent && window.parent !== window) {
            const currentOrigin = window.location.origin;
            
            window.parent.postMessage({
                type: 'MICROFRONTEND_READY',
                source: 'microfrontend-board',
                origin: currentOrigin,
                timestamp: Date.now()
            }, '*');
        }
    }

    // Solicitar token al frontend padre
    requestToken() {
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({
                type: 'REQUEST_AUTH_TOKEN',
                source: 'microfrontend-board',
                origin: window.location.origin,
                timestamp: Date.now()
            }, '*');
        }
    }

    // Notificar al padre que el token expiró
    notifyTokenExpired() {
        if (window.parent && window.parent !== window) {
            console.error('❌ Token expirado. Notificando al frontend padre...');
            
            window.parent.postMessage({
                type: 'AUTH_TOKEN_EXPIRED',
                source: 'microfrontend-board',
                origin: window.location.origin,
                timestamp: Date.now()
            }, '*');
            
            // Limpiar token local
            this.clearToken();
        }
    }

    // Establecer el token recibido
    setToken(token, user = null) {
        this.token = token;
        this.user = user;
        this.isReady = true;

        // Resolver la promesa de token para desbloquear peticiones Apollo en espera
        if (this._tokenPromiseResolve) {
            this._tokenPromiseResolve(token);
            this._tokenPromiseResolve = null;
        }

        // Ejecutar todas las callbacks registradas
        this.tokenReceivedCallbacks.forEach(callback => {
            try {
                callback(token, user);
            } catch (error) {
                console.error('❌ Error en callback de token:', error);
            }
        });
    }

    // Registrar callback para cuando se reciba el token
    onTokenReceived(callback) {
        if (typeof callback === 'function') {
            this.tokenReceivedCallbacks.push(callback);
            
            // Si ya tenemos token, ejecutar callback inmediatamente
            if (this.token) {
                callback(this.token, this.user);
            }
        }
    }

    // Obtener el token actual
    getToken() {
        return this.token;
    }

    // Obtener datos del usuario
    getUser() {
        return this.user;
    }

    // Verificar si hay token disponible
    hasToken() {
        return !!this.token;
    }

    // Esperar a que el token esté disponible (útil para evitar race conditions en Apollo)
    // Retorna una Promesa que resuelve con el token o rechaza tras el timeout
    waitForToken(timeout = 5000) {
        if (this.token) return Promise.resolve(this.token);
        return Promise.race([
            this.tokenPromise,
            new Promise((_, reject) =>
                setTimeout(() => reject(new Error('[microfrontendAuth] Token wait timeout')), timeout)
            )
        ]);
    }

    // Hacer fetch autenticado
    async authenticatedFetch(url, options = {}) {
        if (!this.token) {
            throw new Error('No hay token de autenticación disponible');
        }

        const defaultOptions = {
            headers: {
                'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json',
                ...options.headers
            }
        };

        return fetch(url, { ...options, ...defaultOptions });
    }

    // Limpiar token
    clearToken() {
        this.token = null;
        this.user = null;
        this.isReady = false;
    }
}

// Crear instancia global
window.microfrontendAuth = new MicrofrontendAuthHandler();

export default window.microfrontendAuth;